/**
 * What a failed sign-in says to the person in front of the screen.
 *
 * Two things arrive here, and neither is presentable on its own:
 *
 *  - `signIn()`'s returned code ('CredentialsSignin' for a wrong password).
 *  - `?error=` on /login, because NextAuth's `pages.error` points there. Auth.js
 *    builds that redirect as a bare string interpolation, so an error action
 *    with no type produces the literal `"/login?error=undefined"` — which is
 *    what the storefront was showing (with no message at all, since the page
 *    never read the parameter).
 *
 * Every unrecognized code, including the literal 'undefined', collapses to the
 * ordinary wrong-password sentence: it is true far more often than not, it is
 * the only one a customer can act on, and an internal enum name on a login form
 * is never the right answer.
 */

/** The copy for a rejected email/password pair. Also the fallback for anything unknown. */
export const INVALID_CREDENTIALS_MESSAGE = 'Invalid email or password. Please try again.';

// Null-prototype on purpose. `?error=` is attacker-chosen text, and a plain
// object literal answers 'toString' or 'constructor' with an inherited FUNCTION
// rather than undefined, so the `??` fallback below never fires and the form
// renders "function toString() { [native code] }" at a caller typed `string`.
const MESSAGES_BY_CODE: Record<string, string> = Object.assign(Object.create(null), {
  CredentialsSignin: INVALID_CREDENTIALS_MESSAGE,
  // Auth.js emits this when the provider itself is misconfigured. Nothing the
  // customer typed is wrong, so do not tell them their password is.
  Configuration:
    'We could not sign you in because of a problem on our side. Please try again in a few minutes.',
  AccessDenied: 'This account is not allowed to sign in. Please contact us for help.',
  SessionRequired: 'Please sign in to continue.',
  Verification: 'That sign-in link has expired or has already been used. Please sign in again.',
});

/**
 * The message for an auth error code, or null when there is no error to show.
 *
 * Null only for a genuinely absent code — the string 'undefined' is an ERROR
 * that lost its type on the way here, never the absence of one, and silently
 * showing nothing for it is how a failed login became invisible.
 */
export function getLoginErrorMessage(code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null;

  const trimmed = code.trim();
  if (trimmed.length === 0) return null;

  return MESSAGES_BY_CODE[trimmed] ?? INVALID_CREDENTIALS_MESSAGE;
}
